import React, { SyntheticEvent, useRef, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { useParams } from 'react-router-dom';
import prescriptionUpload from '@custom/rtk-apis/patients/prescriptionUpload';
import prescriptionDelete from '@custom/rtk-apis/patients/prescriptionDelete';
import { closePrescriptionModal } from '@custom/store/slices/Patients/prescriptionUploadSlice';
import { closeModal } from '@custom/store/slices/Patients/patientPrescriptionListSlice';
import prescriptionList from '@custom/rtk-apis/patients/patientPrescriptionList';
import { ICONS, Icon, Loader, SuccessModal, useTheme } from '@ohif/ui';
import { useDropzone } from 'react-dropzone';
import { formatDate } from '@custom/Utils/utils';
import { reset } from '@custom/store/slices/Patients/prescriptionDeleteSlice';

const Prescription = ({ activeTabIndex }) => {
  const { isDarkMode } = useTheme();
  const { uhid } = useParams<{ uhid: string }>();
  const dispatch = useDispatch<AppDispatch>()
  const [prescriptionListData, setPrescriptionList] = useState([])
  const [openIndex, setOpenIndex] = useState(null);
  const [modal, setModal] = useState(false);
  const [title, setTitle] = useState('');
  const [preview, setPreview] = useState('');
  const [prescription, setPrescription] = useState(null);
  const prescriptionListResponse = useSelector((state: RootState) => state?.prescriptionListResponse);
  const prescriptionUploadResponse = useSelector((state: RootState) => state?.prescriptionUploadResponse);
  const prescriptionDeleteResponse = useSelector((state: RootState) => state?.prescriptionDeleteResponse);

  useEffect(() => {
    if (activeTabIndex === 3) {
      callPrescriptionListAPI()
    }
  }, [activeTabIndex])
  useEffect(() => {
    if (prescriptionListResponse?.status === 'SUCCESS') {
      setPrescriptionList(prescriptionListResponse?.result?.list)
      dispatch(closeModal())
    }
  }, [prescriptionListResponse?.status === 'SUCCESS', prescriptionListResponse])

  useEffect(() => {
    if (prescriptionUploadResponse?.status === 'SUCCESS') {
      setModal(true)
      setTitle(prescriptionUploadResponse?.msg)
      setPreview("")
      setTimeout(() => {
        dispatch(closePrescriptionModal())
        setModal(false)
        setTitle("")
        callPrescriptionListAPI()
      }, 2000)
    }
  }, [prescriptionUploadResponse])

  const callPrescriptionListAPI = () => {
    const hospitalId = sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId");
    let obj = {
      hospitalId: hospitalId,
      uhid: uhid,
      limit: 10,
      pageNumber: 1
    }
    dispatch(prescriptionList(obj))
  }

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file); // Convert the file to Base64
    }
    setPrescription(file);
  }, []);

  const handleUploadPrescription = async (event, folderPath) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {

        const formData = new FormData();
        formData.append('hospitalId', sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId"));
        formData.append('uhid', uhid);
        formData.append('documentName', file.name);
        formData.append('documentData', file);
        formData.append('folderName', folderPath);

        dispatch(prescriptionUpload(formData))
          .unwrap()
          .then((result) => {
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
          });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrescriptionDelete = (id) => () => {
    const obj = {
      prescriptionId: id
    }
    dispatch(prescriptionDelete(obj))
  }

  useEffect(() => {
    if (prescriptionDeleteResponse?.status === 'SUCCESS') {
      setTitle('Prescription deleted successfully.')
      setModal(true)
      setTimeout(() => {
        dispatch(closePrescriptionModal())
        setModal(false)
        setTitle("")
        dispatch(reset())
        callPrescriptionListAPI()
      }, 800)
    } else if (prescriptionDeleteResponse?.status === 'FAILED') {
      setTimeout(() => {
        dispatch(reset())
      }, 800)
    }
  }, [prescriptionDeleteResponse?.status])

  return (
    <>
      <div className='mx-4 mt-8'>
        {
          prescriptionListData?.map((item, index) => (
            <div key={index} className='rounded-lg'>
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full accordion text-left px-4 py-2 bg-gray-100 rounded-t-lg hover:bg-gray-200"
                style={{ backgroundColor: isDarkMode ? '#090c29' : '#f7f7f7' }}
              >
                <span className='accordion-date'>{formatDate(item?.createdAt,true)}</span>

                <span className='flex items-center gap-4'>
                  <img src={ICONS.delete} alt="Delete" onClick={handlePrescriptionDelete(item._id)} ></img>
                  <img src={ICONS.downarrow} alt="down arrow" />
                </span>
              </button>
              {
                openIndex === index && (
                  <div className="p-4 bg-white border-t accordion-image">
                    <img src={item.documentData}></img>
                  </div>
                )
              }
            </div>
          ))
        }
        <div className='mt-8'>
          <span className="upload-prescription-text">Upload Prescription<span className="required-asterisk">*</span></span>
          <div className="uploadDiv">

            <div className='cursor-pointer relative'>
              <SignatureDropzone onDrop={(acceptedFiles) => onDrop(acceptedFiles)}
                preview={preview}
              />
            </div>
          </div>
          <button
              className='btn btn__primary mt-4'
              onClick={() => handleUploadPrescription({ target: { files: [prescription] } }, 'prescription')}
          >
            Upload
          </button>
        </div>

      </div>
      <SuccessModal
        isSuccessModalOpen={modal}
        title={title}
        handleCloseModal={() => setModal(false)}
      />
      <Loader
        isLoaderOpen={prescriptionUploadResponse?.loading || prescriptionListResponse?.loading || prescriptionDeleteResponse?.loading}
        handleCloseModal={() =>{}}
        title="Uploading..."
      />
    </>
  )

}

export const SignatureDropzone = ({ onDrop, preview }) => {
  const [error, setError] = useState(null);
  const { isDarkMode } = useTheme();
  const handleDrop = (acceptedFiles) => {
      const maxSize = 50 * 1024 * 1024; // 3 MB
      const validFiles = [];

      setError(null);

      for (const file of acceptedFiles) {
          if (file.size <= maxSize) {
              validFiles.push(file);
          } else {
              // Remove previous image and show error
              onDrop([]); // Clear previous images
              setError(`File ${file.name} is too large. Maximum image size is 50 MB.`);
              break; // Exit loop after showing error
          }
      }

      if (validFiles.length > 0) {
          onDrop(validFiles); // Send valid files to the parent
      }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: handleDrop,
      accept: {
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/png': ['.png'],
      },
  });

  return (
      <div
          {...getRootProps()}
          style={{
              border: '2px dashed #cccccc',
              padding: '20px',
              textAlign: 'center',
              marginBottom: '10px',
              width: '100%',
              height: '150px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: isDragActive ? '#f0f0f0' : 'white',
              position: 'relative',
              overflow: 'hidden',
          }}
      >
          <input {...getInputProps()} />
          <div className='text-center flex flex-col items-center justify-center'>
              {!preview ? (
                  <>
                      <Icon name="Upload_Icon" alt="appointmentPreview" />
                      <p style={{ color: '#000000' }}>Drag & Drop files or <span className='main-theme-color underline' style={{ textDecoration: 'underline' }}>Browse</span></p>
                      <p style={{ color: '#000000' }}>Supported format: <span className='main-theme-color'>JPEG, PNG</span></p>
                  </>
              ) : (
                  <img
                      src={preview}
                      alt="Signature Preview"
                      style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                      }}
                  />
              )}
              {error && <p className="text-red-500">{error}</p>}
          </div>
      </div>
  );
};

export default Prescription
